// Order sets R1 (PROD-2723, v3 board 6 "ApAfter"): what is NESTED UNDER A DIAGNOSIS on the A/P.
// The DS canvas draws each diagnosis once, as a card (`AssessmentPlanCanvas dxLayout="stacked"`),
// and renders this body inside it: the "No medication checks" notice (PROD-2721) when a set applied
// here placed a medication, the diagnosis's orders, then the "Not ordered from <set>:" list of each
// set applied to it (duplicates with "Order anyway" included). The grouping is pure
// (`ap-dx-groups.ts`); the order rows are the host's own (`renderOrders`), so a nested row is the
// same row, with the same press-to-edit, bin and diagnosis-link affordances, as anywhere else on
// the A/P.
//
// PROD-2850 (board 6): an education article a set attached renders in the card of the diagnosis
// the set was applied to, after the orders and before the set's list ("Living with type 2 diabetes
// · EDU · Article · sent after signing"). Its remove is the Education section's own.
import type { ReactNode } from 'react';
import type { PlanOrder } from '../lib/to-patient-assessment';
import type { DxOrderGroup } from './ap-dx-groups';
import { OrderSetLeftovers } from './order-set-canvas-blocks';
import { ApEducationRow } from './ap-education-row';
import { NoMedicationChecksNotice } from '../no-medication-checks-notice';
import type { OrderSetApplyState } from './use-order-set-apply';

export interface ApDxOrderBodyProps {
  group: DxOrderGroup | undefined;
  /** The host's own order rows for a run of orders (same rows as the flat list). */
  renderOrders: (orders: PlanOrder[]) => ReactNode;
  state: OrderSetApplyState;
  readOnly?: boolean;
  /** The A/P's article remove (id when saved, else its index in the A/P's article list). */
  onRemoveArticle?: (idOrIndex: string | number) => void;
}

/** The body of one diagnosis card; null when nothing sits under the diagnosis yet. */
export function ApDxOrderBody({ group, renderOrders, state, readOnly, onRemoveArticle }: ApDxOrderBodyProps) {
  if (!group || (group.orders.length === 0 && group.leftovers.length === 0 && group.articles.length === 0)) return null;
  return (
    <section
      data-slot="ap-dx-order-group"
      aria-label={`Orders for ${group.dx.display}`}
      className="flex flex-col gap-2"
    >
      {/* PROD-2721 AC1: one notice per card, however many sets placed a medication here. */}
      {group.notices.length > 0 && <NoMedicationChecksNotice />}
      {group.orders.length > 0 && renderOrders(group.orders)}
      {group.articles.map(({ article, index }) => (
        <ApEducationRow
          key={article.id ?? `article-${index}`}
          article={article}
          readOnly={readOnly}
          onRemove={onRemoveArticle ? () => onRemoveArticle(article.id ?? index) : undefined}
        />
      ))}
      {group.leftovers.map((app) => (
        <OrderSetLeftovers key={app.key} app={app} state={state} readOnly={readOnly} />
      ))}
    </section>
  );
}
